import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FaEye,
  FaEdit,
  FaTrash,
  FaThLarge,
  FaArrowLeft,
} from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import Search from "../../../../common/Search";
import {
  SectionList,
  DeleteSection,
  SectionChangeStatus,
} from "../../../../redux/services/ContentService/content.Services";
import Pagination from "../../../../common/Pagination";
import { successToast, errorToast } from "../../../../Utils/toast";

function Sectionlist() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [selectedSolution, setSelectedSolution] = useState(null);
  const [selectedModule, setSelectedModule] = useState(null);

  useEffect(() => {
    const storedSolution = localStorage.getItem("selectedSolution");
    const storedModule = localStorage.getItem("selectedModule");

    if (storedSolution) setSelectedSolution(JSON.parse(storedSolution));
    if (storedModule) setSelectedModule(JSON.parse(storedModule));
  }, []);

  const solutionId = selectedSolution?.id;

  const [totalRecords, setTotalRecords] = useState(0);
  const [sectionlist, setSectionList] = useState([]);
  const [limit, setLimit] = useState(100);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const refreshData = useSelector((state) => state.productModal.refreshData);

  const fetchSection = async () => {
    if (!solutionId) return;
    try {
      setLoading(true);
      const payload = { page, search, limit, solution_id: solutionId };
      const res = await SectionList(payload);
      setSectionList(res?.data || []);
      setLastPage(res?.last_page || 1);
      setTotalRecords(res?.total || 0);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSection();
    // eslint-disable-next-line
  }, [page, search, solutionId, refreshData]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= lastPage) setPage(newPage);
  };

  const handleViewItems = (section) => {
    localStorage.setItem("selectedSection", JSON.stringify(section));
    navigate("/items-list");
  };

  const handleAddSection = () => {
    navigate("/add-section");
  };

  const handleViewSection = (item) => {
    localStorage.setItem("viewSection", JSON.stringify(item));
    navigate("/view-section");
  };

  const handleEditSection = (item) => {
    localStorage.setItem("editSection", JSON.stringify(item));
    navigate("/edit-section");
  };

  const handleDeleteSection = async (item) => {
    const confirmDelete = window.confirm(
      `Are you sure you want to delete "${item.section_key || item.title}"?`
    );
    if (!confirmDelete) return;

    try {
      const res = await DeleteSection({ id: item.id });

      if (res?.success) {
        successToast(res?.message || "Section deleted successfully!");
        fetchSection();
      } else {
        errorToast(res?.message || "Failed to delete section");
      }
    } catch (err) {
      console.log(err);
      errorToast(err?.response?.data?.message || "Something went wrong");
    }
  };

  const handleToggleStatus = async (item) => {
    const confirmToggle = window.confirm(
      `Change status of "${item.section_key || item.title}"?`
    );
    if (!confirmToggle) return;

    try {
      const newStatus = item.status == 1 ? 0 : 1;
      const res = await SectionChangeStatus({
        id: item.id,
        status: newStatus,
      });

      if (res?.success) {
        successToast(res?.message || "Status changed successfully!");
        fetchSection();
      } else {
        errorToast(res?.message || "Failed to change status");
      }
    } catch (err) {
      console.log(err);
      errorToast(err?.response?.data?.message || "Something went wrong");
    }
  };

  return (
    <div>
      <div className="d-md-flex d-block align-items-center justify-content-between mt-1 page-header-breadcrumb">
        <div className="d-flex justify-content-between align-items-center w-100">
          <div className="page-header">
            <div>
              <h3 className="page-title mb-0">
                Section List
                {selectedSolution && (
                  <span className="text-muted ms-2">
                    — {selectedSolution.name}
                  </span>
                )}
              </h3>
              <nav className="page-breadcrumb">
                <Link to="/">Home</Link>
                <span className="mx-2">/</span>
                <Link to="/module-list">Modules</Link>
                <span className="mx-2">/</span>
                {selectedModule && (
                  <>
                    <span>{selectedModule.name}</span>
                    <span className="mx-2">/</span>
                  </>
                )}
                <Link to="/solutions-list">Solutions</Link>
                <span className="mx-2">/</span>
                <span>Sections</span>
              </nav>
            </div>
          </div>

          <div className="d-flex align-items-center gap-2">
            <button className="add-btn" onClick={handleAddSection}>
              + Add Section
            </button>

            <Link
              to="/solutions-list"
              className="btn btn-sm btn-light d-flex align-items-center justify-content-center"
              style={{ width: "40px", height: "40px", borderRadius: "8px" }}
            >
              <FaArrowLeft />
            </Link>
          </div>
        </div>
      </div>

      <div className="main_page">
        {!solutionId && (
          <div className="alert alert-warning mx-2">
            ⚠️ No solution selected. Please go back and select a solution.
          </div>
        )}

        {(totalRecords > 0 || search) && (
          <div className="d-flex justify-content-end align-items-center mx-2 mb-2">
            <Search
              search={search}
              placeholder="Search Section..."
              onSearch={(value) => {
                setSearch(value);
                setPage(1);
              }}
            />
          </div>
        )}

        <div className="st-wrapper-card text-center">
          <div className="st-table-responsive">
            <table className="table st-custom-table align-middle mb-0">
              <thead>
                <tr>
                  <th>Sr</th>
                  <th>Section Key</th>
                  <th>Title</th>
                  <th>Type</th>
                  <th>Change Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {sectionlist.length > 0 ? (
                  sectionlist.map((item, index) => (
                    <tr key={item.id}>
                      <td>{index + 1 + (page - 1) * limit}</td>
                      <td>
                        <span className="">
                          {item.section_key}
                        </span>
                      </td>
                      <td>{item.title}</td>
                      <td>
                        <span className="">{item.type}</span>
                      </td>
                      <td>
                        <div className="city-status-switch">
                          <input
                            type="checkbox"
                            id={`status-${item.id}`}
                            className="city-status-input"
                            checked={item.status == 1}
                            onChange={() => handleToggleStatus(item)}
                          />
                          <label
                            htmlFor={`status-${item.id}`}
                            className="city-status-label"
                          >
                            <span className="city-status-ball"></span>
                            <span className="city-status-text">
                              {item.status == 1 ? "Active" : "Inactive"}
                            </span>
                          </label>
                        </div>
                      </td>
                      <td className="text-center">
                        <div className="d-flex align-items-center justify-content-center gap-1">
                          <button
                         className="st-action-btn st-view-btn"
                            title="View Items"
                            onClick={() => handleViewItems(item)}
                           
                          >
                            <FaThLarge />
                          </button>

                          <button
                            className="st-action-btn st-view-btn"
                            onClick={() => handleViewSection(item)}
                          >
                            <FaEye />
                          </button>

                          <button
                            className="st-action-btn st-edit-btn"
                            onClick={() => handleEditSection(item)}
                          >
                            <FaEdit />
                          </button>

                          <button
                            className="st-action-btn st-delete-btn"
                            onClick={() => handleDeleteSection(item)}
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="text-center py-5">
                      <h5 className="mb-0">
                        {!solutionId ? "No solution selected" : "No Data Found"}
                      </h5>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            {lastPage > 1 && (
              <div className="d-flex justify-content-end mt-3">
                <Pagination
                  page={page}
                  totalPages={lastPage}
                  onPageChange={handlePageChange}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Sectionlist;